/* eslint-disable react/jsx-props-no-spreading */
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import CustomModalHeader from 'components/modal/customModalHeader';
import GroupButton from 'components/button/groupButton';
import { toast } from 'react-toastify';
import { messageErrors } from 'utils/utils';
import useUser from 'hooks/useUser';
import { Modal } from 'flowbite-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { useEffect, useRef } from 'react';
import { themModal } from 'utils/theme';
import '../../form/styles.scss';
import InputText from 'components/commonComponent/inputText';
import MultiSelect from 'components/select/index';
import { orgSelector } from 'redux/selectors';
import { useSelector } from 'react-redux';
import { createCategory, getCategoriesByFilter, updateCategoryById } from 'api/categoryApi';
import { defaultpageCount } from 'utils/constants';

type categoryModalProps = {
  openModal: boolean,
  setOpenModal: Function,
  headerTitle?: string,
  targetData?: any,
  isEdit: boolean,
  queryClient?: any,
  setCurrentPage?: any,
  currentPage?: number,
};

const CategoryModal = (props: categoryModalProps) => {
  const { openModal, targetData, isEdit, setOpenModal, headerTitle, currentPage, queryClient, setCurrentPage } = props;
  const { fetchCurrentUser } = useUser();
  const [t] = useTranslation();
  const rootRef = useRef(null);
  const { organizationId } = useSelector(orgSelector);

  const ValidateSchema = Yup.object().shape({
    name: Yup.string().required(t('userManagementPage.requiredField')).max(150, t('locationModal.maxLength150')),
  });

  const hookForm = useForm({
    mode: 'onChange',
    resolver: yupResolver(ValidateSchema),
  });

  const { handleSubmit, setValue, reset, watch } = hookForm;
  const watchedParentId = watch('parentId');

  const { data: categoriesData } = useQuery(
    ['getCategoriesForParentSelect', organizationId],
    () => getCategoriesByFilter({ organizationId, limit: defaultpageCount, page: 0 }),
    { staleTime: Infinity },
  );

  const parentOptions = (categoriesData?.data?.entities || [])
    .filter((c: any) => (!isEdit || c.id !== targetData?.id) && !c.parentId && !c.parent?.id)
    .map((c: any) => ({ value: c.id, label: c.name }));

  useEffect(() => {
    if (isEdit) {
      reset(targetData);
      setValue('name', targetData.name);
      setValue('parentId', targetData.parentId || null);
    } else {
      reset();
    }
    // eslint-disable-next-line
  }, []);

  const handleAddAndUpdateCategory = async (payload: any) => {
    payload.organizationId = organizationId;
    payload.organization = { id: organizationId };
    payload.parentId = payload.parentId || null;

    if (isEdit) {
      await updateCategoryById(targetData.id, payload);
    } else {
      await createCategory(payload);
    }
  };

  const mutation = useMutation(['create-update-category'], { mutationFn: handleAddAndUpdateCategory });

  const onSubmit = (values: any) => {
    mutation.mutate(values, {
      onSuccess: () => {
        toggleHandler();
        queryClient.invalidateQueries(['getCategories']);
        queryClient.invalidateQueries(['getCategoriesForParentSelect']);
        setCurrentPage(0);
        const message: string = isEdit ? t('categoryPage.editSuccess') : t('categoryPage.saveSuccess');
        toast.success(message);
        fetchCurrentUser();
      },
      onError: (error: any) => {
        const message: string = messageErrors(error, t);
        toast.error(message);
      },
    });
  };

  const toggleHandler = () => {
    setOpenModal(!openModal);
    reset();
  };

  return (
    <div ref={rootRef} className="category-modal-container">
      <Modal
        show={openModal}
        size="xl"
        className="category-modal"
        root={document.body}
        onClose={toggleHandler}
        dismissible={true}
        theme={themModal}
      >
        <CustomModalHeader title={headerTitle} toggle={toggleHandler} />
        <Modal.Body theme={{ base: 'flex-12 pb-4 pt-3 px-12' }}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="my-4 relative">
              <InputText
                hookForm={hookForm}
                placeholder={`${t('categoryPage.modalCategoryName')}`}
                name="name"
                id="name"
                isHolderLabel={true}
              />
            </div>
            <div className="my-4 relative">
              <MultiSelect
                hookForm={hookForm}
                name="parentId"
                placeholder={t('categoryPage.modalParentCategory')}
                isHolderLabel={true}
                options={parentOptions}
                value={parentOptions.find((o: any) => o.value === watchedParentId) || null}
                onChange={(option: any) => setValue('parentId', option ? option.value : null)}
                isClearable={true}
              />
            </div>
            <GroupButton
              className="items-center justify-center pt-5 mb-4"
              buttons={[
                {
                  type: 'button',
                  text: t('modal.cancel'),
                  classType: 'white',
                  action: () => toggleHandler(),
                },
                {
                  type: 'submit',
                  text: t('modal.save'),
                  classType: 'blue',
                  isLoading: mutation.isLoading,
                },
              ]}
            />
          </form>
        </Modal.Body>
      </Modal>
    </div>
  );
};
export default CategoryModal;
